import { useEffect, useState } from "react";
import { Container, Grid } from "@material-ui/core";
import {
  OutlinedFlag,
  AttachMoney,
  TrendingUp,
  CardGiftcard,
} from "@material-ui/icons";
import Api from "lib/api/httpRequestHelper";
import getMonthByNumber from "utils/get-month-by-number";
import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import MonthYearField from "components/_refactor/fields/month-year";
import StructureFields from "components/_refactor/fields/structure";
import FilterCard from "components/_refactor/filter-card";
import HeaderClientes from "components/_refactor/headers/clients";
import BigNumberCard from "components/_refactor/card/big-number";
import TableRelatorioPerformance from "./components/table-relatorio";

const STATUS_OPTIONS = [
  { id: "TODOS", name: "Todos" },
  { id: "Meta Batida", name: "Meta Batida" },
  { id: "Meta Não Batida", name: "Meta Não Batida" },
];

const MONTH_INDEX = new Date().getMonth() + 1;
const YEAR = new Date().getFullYear();

export default function RelatorioPerformance() {
  const [results, setResults] = useState([]);
  const [totalizadores, setTotalizadores] = useState(null);
  const [searching, setSearching] = useState(false);

  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [month, setMonth] = useState({
    value: MONTH_INDEX,
    label: getMonthByNumber(MONTH_INDEX),
  });
  const [year, setYear] = useState({ value: YEAR, label: String(YEAR) });
  const [clearFilters, setClearFilters] = useState(false);

  const snackbar = useSnackbar();

  const getSellers = async () => {
    setLoadingSellers(true);
    try {
      const response = await Api.get(
        "seller-list",
        "action",
        `?IdPessoaLoja=${structureFilters.idLojaUnica}`,
        true
      );
      setSellers(response?.Result?.ListaVendedores || []);
    } catch (error) {
      snackbar.showMessage("Erro ao buscar os vendedores: " + error.message, {
        severity: "error",
      });
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleSearch = async () => {
    setSearching(true);

    try {
      const body = {
        idLoja: structureFilters?.idLojaUnica,
        mes: month.value,
        ano: year.value,
        idVendedor: selectedSeller?.IdLojaVendedor,
        status: status.id,
      };

      const response = await Api.get(
        "performance-goal-store-report",
        "goals",
        UtilsFncs.RestParamsComposer(body),
        true
      );
      setResults(response?.Result?.Itens || []);
      setTotalizadores(response?.Result?.Totalizadores || null);
    } catch (error) {
      snackbar.showMessage("Erro ao buscar as metas: " + error.message, {
        severity: "error",
      });
    } finally {
      setSearching(false);
    }
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setMonth("");
    setYear("");
    setStructureFilters({});
  };

  useEffect(() => {
    if (structureFilters.idLojaUnica) {
      getSellers();
    }
  }, [structureFilters, month, year]);

  return (
    <>
      <HeaderClientes
        section="Relatório de Performance de Vendas"
        subsection="Metas"
      />
      <Container style={{ marginTop: "-4rem" }} maxWidth={false}>
        <FilterCard
          handleSearch={handleSearch}
          disabled={
            searching ||
            structureLoading.length > 0 ||
            structureFilters.idLojaUnica === null ||
            !month ||
            !year
          }
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            fieldsOrder={["economicGroup", "storeSingle"]}
            onStructureFieldsChange={setStructureFilters}
            disableAll={searching}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={["economicGroup", "storeSingle"]}
            hideFields={["channel", "representative", "store"]}
            clearFilters={clearFilters}
            handleSearch={handleSearch}
            showSingleStore
          />
          <Grid item xs={12} md={3}>
            <AutoCompleteField
              label="Vendedores"
              options={sellers}
              onChange={(value) => {
                setSelectedSeller(value);
              }}
              loading={loadingSellers}
              disabled={loadingSellers || sellers.length === 0}
              value={selectedSeller}
              optionIdKey="IdLojaVendedor"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AutoCompleteField
              label="Status"
              options={STATUS_OPTIONS}
              onChange={(value) => {
                setStatus(value);
              }}
              disabled={searching}
              value={status}
              optionLabelKey="name"
              optionIdKey="id"
            />
          </Grid>
          <MonthYearField
            month={month}
            handleMonthChange={setMonth}
            year={year}
            handleYearChange={setYear}
            disabled={searching}
            required
            direction="both"
            yearsCount={2}
            lg={6}
            md={6}
          />
        </FilterCard>

        {searching && (
          <LoadingData searching={searching} label="Buscando dados..." />
        )}

        {!searching && totalizadores && (
          <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
            <Grid item xs={12} md={3}>
              <BigNumberCard
                title="Meta Total da Loja"
                value={MaskAndValid.maskPrice(totalizadores.TotalMeta)}
                loading={searching}
                icon={<OutlinedFlag size="large" color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <BigNumberCard
                title="Realizado Total"
                value={MaskAndValid.maskPrice(totalizadores.TotalRealizado)}
                loading={searching}
                icon={<AttachMoney size="large" color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <BigNumberCard
                title="Atingimento Geral"
                value={`${totalizadores.TotalAtingimento?.toFixed(2) || 0}%`}
                loading={searching}
                icon={<TrendingUp size="large" color="primary" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <BigNumberCard
                title="Total de Bonificações"
                value={MaskAndValid.maskPrice(totalizadores.TotalBonificacao)}
                loading={searching}
                icon={<CardGiftcard size="large" color="primary" />}
              />
            </Grid>
          </Grid>
        )}

        {!searching && (
          <Grid item xs={12} style={{ marginBottom: "1rem" }}>
            <TableRelatorioPerformance
              dataTable={results}
              periodo={`${getMonthByNumber(month.value)}/${year.value}`}
            />
          </Grid>
        )}
      </Container>
    </>
  );
}
